(function () {
  const SCROLL_EPSILON = 2;

  function getMaxScrollLeft(carousel) {
    return Math.max(0, carousel.scrollWidth - carousel.clientWidth);
  }

  function getSlideScrollLeft(carousel, slide) {
    const carouselRect = carousel.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    return carousel.scrollLeft + (slideRect.left - carouselRect.left);
  }

  function getSlideScrollTargets(carousel, slides) {
    const maxScroll = getMaxScrollLeft(carousel);

    return slides.map((slide) => {
      const ideal = getSlideScrollLeft(carousel, slide);
      return Math.min(Math.max(0, ideal), maxScroll);
    });
  }

  function getActiveSlideIndex(carousel, slides, targets) {
    const scrollLeft = carousel.scrollLeft;
    const maxScroll = getMaxScrollLeft(carousel);

    if (slides.length <= 1 || maxScroll <= SCROLL_EPSILON) {
      return 0;
    }

    if (scrollLeft >= maxScroll - SCROLL_EPSILON) {
      return slides.length - 1;
    }

    let activeIndex = 0;

    for (let i = 0; i < targets.length - 1; i++) {
      const current = targets[i];
      const next = targets[i + 1];

      if (Math.abs(current - next) <= SCROLL_EPSILON) {
        if (scrollLeft >= current - SCROLL_EPSILON) {
          activeIndex = i + 1;
        }
        continue;
      }

      const midpoint = (current + next) / 2;
      if (scrollLeft >= midpoint) {
        activeIndex = i + 1;
      }
    }

    return activeIndex;
  }

  function setActiveDot(dots, activeIndex) {
    dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('press-mention__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  function updateActiveDot(carousel, dots, slides) {
    const targets = getSlideScrollTargets(carousel, slides);
    const activeIndex = getActiveSlideIndex(carousel, slides, targets);
    setActiveDot(dots, activeIndex);
  }

  function updateCarouselCenteringState(carousel) {
    const scrollable = carousel.scrollWidth > carousel.clientWidth + SCROLL_EPSILON;
    carousel.classList.toggle('press-mention__carousel--centered', !scrollable);
    return scrollable;
  }

  const DESKTOP_MEDIA = window.matchMedia('(min-width: 750px)');

  function isAutoplayEnabledForViewport(wrapper) {
    const isDesktop = DESKTOP_MEDIA.matches;
    return isDesktop
      ? wrapper.dataset.autoRotateDesktop === 'true'
      : wrapper.dataset.autoRotateMobile === 'true';
  }

  function initAutoplay(wrapper, carousel, slides, scrollToSlide, getScrollable) {
    const desktopEnabled = wrapper.dataset.autoRotateDesktop === 'true';
    const mobileEnabled = wrapper.dataset.autoRotateMobile === 'true';
    if (!desktopEnabled && !mobileEnabled) return null;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

    const speedSec = Math.max(2, parseInt(wrapper.dataset.autoRotateSpeed, 10) || 4);
    let timer = null;
    let paused = false;

    const canAutoplay = () =>
      !paused && getScrollable() && isAutoplayEnabledForViewport(wrapper);

    const advance = () => {
      if (!canAutoplay()) return;

      const targets = getSlideScrollTargets(carousel, slides);
      const current = getActiveSlideIndex(carousel, slides, targets);
      const next = current >= slides.length - 1 ? 0 : current + 1;
      scrollToSlide(next);
    };

    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      if (!canAutoplay()) return;
      timer = setInterval(advance, speedSec * 1000);
    };

    const pause = () => {
      paused = true;
      stop();
    };

    const resume = () => {
      paused = false;
      start();
    };

    const restart = () => {
      if (paused) return;
      start();
    };

    wrapper.addEventListener('mouseenter', pause);
    wrapper.addEventListener('mouseleave', resume);
    wrapper.addEventListener('focusin', pause);
    wrapper.addEventListener('focusout', (event) => {
      if (!wrapper.contains(event.relatedTarget)) resume();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) pause();
      else resume();
    });

    carousel.addEventListener(
      'touchstart',
      () => {
        pause();
      },
      { passive: true }
    );

    carousel.addEventListener(
      'touchend',
      () => {
        resume();
      },
      { passive: true }
    );

    const onViewportChange = () => {
      if (canAutoplay()) start();
      else stop();
    };

    if (typeof DESKTOP_MEDIA.addEventListener === 'function') {
      DESKTOP_MEDIA.addEventListener('change', onViewportChange);
    } else if (typeof DESKTOP_MEDIA.addListener === 'function') {
      DESKTOP_MEDIA.addListener(onViewportChange);
    }

    start();

    return { start, stop, pause, resume, restart };
  }

  function initPressMentionSlider(wrapper) {
    if (wrapper.dataset.pressMentionDotsInit === 'true') return;
    wrapper.dataset.pressMentionDotsInit = 'true';

    const carousel = wrapper.querySelector('.press-mention__carousel[id^="Slider-"]');
    const dots = Array.from(wrapper.querySelectorAll('.press-mention__dot'));
    const slides = Array.from(wrapper.querySelectorAll('.press-mention__item[id^="Slide-"]'));

    if (!carousel || slides.length < 2) return;

    let pendingActiveIndex = null;
    let scrollEndTimeout = null;
    let isScrollable = false;

    const getScrollable = () => carousel.scrollWidth > carousel.clientWidth + SCROLL_EPSILON;

    const clearPendingActiveIndex = () => {
      pendingActiveIndex = null;
      updateActiveDot(carousel, dots, slides);
    };

    const scheduleClearPending = () => {
      if (scrollEndTimeout) {
        clearTimeout(scrollEndTimeout);
      }

      scrollEndTimeout = setTimeout(clearPendingActiveIndex, 400);
    };

    let autoplay = null;

    const scrollToSlide = (index) => {
      const slide = slides[index];
      if (!slide) return;

      const targets = getSlideScrollTargets(carousel, slides);
      const targetScroll = targets[index] ?? 0;

      pendingActiveIndex = index;
      setActiveDot(dots, index);

      carousel.scrollTo({
        left: targetScroll,
        behavior: 'smooth',
      });

      scheduleClearPending();
      autoplay?.restart();
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        const index = parseInt(dot.dataset.slideIndex, 10);
        if (Number.isNaN(index)) return;
        scrollToSlide(index);
      });
    });

    carousel.addEventListener(
      'scroll',
      () => {
        if (pendingActiveIndex !== null) {
          setActiveDot(dots, pendingActiveIndex);
          return;
        }

        updateActiveDot(carousel, dots, slides);
      },
      { passive: true }
    );

    if ('onscrollend' in window) {
      carousel.addEventListener('scrollend', clearPendingActiveIndex, { passive: true });
    }

    const onResize = () => {
      isScrollable = updateCarouselCenteringState(carousel);

      if (pendingActiveIndex !== null) {
        setActiveDot(dots, pendingActiveIndex);
      } else {
        updateActiveDot(carousel, dots, slides);
      }

      if (isScrollable) autoplay?.start();
      else autoplay?.stop();
    };

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(carousel);
    }

    window.addEventListener('resize', onResize, { passive: true });
    onResize();

    autoplay = initAutoplay(wrapper, carousel, slides, scrollToSlide, getScrollable);
  }

  function initAll(root = document) {
    root.querySelectorAll('.press-mention__slider-wrapper').forEach(initPressMentionSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
